import { useMemo, useState } from "react";
import TreeNode from "../TreeNode/TreeNode";
import useAssetTree from "./useCustomAssetTree";
import { useAppDispatch } from '@store';
import AssetHeader from "./AssetsComponents/Header/Header";
import AssetSearch from "./AssetsComponents/Search/Search";
import { Spinner } from "@rio-cloud/rio-uikit";

export type Vehicle = {
  id: string;
  name: string;
};

export type Contract = {
  id: string;
  name: string;
  vehicles: Vehicle[];
};

export type Client = {
  id: string;
  name: string;
  contracts: Contract[];
};

export type IRegionalProps = {
  id: string;
  name: string;
  clients: Client[];
};


const CustomAssetTree = () => {

  const dispatch = useAppDispatch()

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAsset, setShowAsset] = useState(false)
  const [search, setSearch] = useState('');

  const {
    selectedRegional,
    selectedClients,
    selectedContracts,
    selectedVehicles,

    setSelectedVehicles,
    setSelectedContracts,
    setSelectedClients,
    setSelectedRegional,
    assetData,
    loading
  } = useAssetTree()

  const filterItems = (
    data: IRegionalProps[],
    search: string
  ): IRegionalProps[] => {
    if (!search.trim()) return data;

    const term = search.toLowerCase();
    const matchesTerm = (name: string) => name.toLowerCase().includes(term);

    return data
      .map(regional => {
        if (matchesTerm(regional.name)) return regional;

        const clients = regional.clients
          .map(client => {
            if (matchesTerm(client.name)) return client;

            const contracts = client.contracts
              .map(contract => {
                if (matchesTerm(contract.name)) return contract;

                const vehicles = contract.vehicles.filter(vehicle =>
                  matchesTerm(vehicle.name)
                );

                return vehicles.length > 0 ? { ...contract, vehicles } : null;
              })
              .filter(Boolean) as Contract[];

            return contracts.length > 0 ? { ...client, contracts } : null;
          })
          .filter(Boolean) as Client[];

        return clients.length > 0 ? { ...regional, clients } : null;
      })
      .filter(Boolean) as IRegionalProps[];
  };

  const filteredData = useMemo(() => {
    if(!assetData) return;
    return filterItems(assetData?.regionals, search);
  }, [assetData, search]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectRegional = (id: string) => {
    if(!filteredData || !assetData) return;

    const data = filteredData || assetData?.regionals;

    const newData = data.filter(regional => regional?.id === id)
    const { regionals, clients, contracts, vehicles } = collectAllIds(newData);

    if(selectedRegional.includes(id)) {
      const regional = selectedRegional.filter(item => item !== id)
      const clientsNew = selectedClients.filter(item => !clients.includes(item))
      const contractsNew = selectedContracts.filter(item => !contracts.includes(item))
      const vehiclesNew = selectedVehicles.filter(item => !vehicles.includes(item))

      dispatch(setSelectedRegional(regional));
      dispatch(setSelectedClients(clientsNew));
      dispatch(setSelectedContracts(contractsNew));
      dispatch(setSelectedVehicles(vehiclesNew));

      return
    }

    dispatch(
      setSelectedRegional(
        Array.from(new Set([...selectedRegional, ...regionals]))
      )
    );

    dispatch(
      setSelectedClients(
        Array.from(new Set([...selectedClients, ...clients]))
      )
    );

    dispatch(
      setSelectedContracts(
        Array.from(new Set([...selectedContracts, ...contracts]))
      )
    );

    dispatch(
      setSelectedVehicles(
        Array.from(new Set([...selectedVehicles, ...vehicles]))
      )
    );

  };

  const toggleSelectClients = (id: string) => {
    if(!filteredData || !assetData) return;

    const data = filteredData || assetData?.regionals;

    const newData = data?.map(regional => {
      const clients = regional.clients.map(client => {
        if (client.id === id) return client

        return null
      }).filter(Boolean) as Client[];

      return { ...regional, clients }
    })

    const { clients, contracts, vehicles } = collectAllIds(newData);

    if(selectedClients.includes(id)) {
      const clientsNew = selectedClients.filter(item => !clients.includes(item))
      const contractsNew = selectedContracts.filter(item => !contracts.includes(item))
      const vehiclesNew = selectedVehicles.filter(item => !vehicles.includes(item))

      dispatch(setSelectedClients(clientsNew));
      dispatch(setSelectedContracts(contractsNew));
      dispatch(setSelectedVehicles(vehiclesNew));

      return
    }


    dispatch(
      setSelectedClients(
        Array.from(new Set([...selectedClients, ...clients]))
      )
    );

    dispatch(
      setSelectedContracts(
        Array.from(new Set([...selectedContracts, ...contracts]))
      )
    );

    dispatch(
      setSelectedVehicles(
        Array.from(new Set([...selectedVehicles, ...vehicles]))
      )
    );
  };

  const toggleSelectContracts = (id: string) => {
    if(!filteredData || !assetData) return;

    const data = filteredData || assetData?.regionals;

    const newData = data.map(regional => {
      const clients = regional.clients
        .map(client => {
          const contracts = client.contracts.filter(
            contract => contract.id === id
          )

          return { ...client, contracts }
        })
        .filter(client => client.contracts.length > 0)

      return { ...regional, clients }
    }).filter(regional => regional.clients.length > 0)


    const { contracts, vehicles } = collectAllIds(newData);

    if(selectedContracts.includes(id)) {
      const contractsNew = selectedContracts.filter(item => !contracts.includes(item))
      const vehiclesNew = selectedVehicles.filter(item => !vehicles.includes(item))

      dispatch(setSelectedContracts(contractsNew));
      dispatch(setSelectedVehicles(vehiclesNew));

      return
    }

    dispatch(
      setSelectedContracts(
        Array.from(new Set([...selectedContracts, ...contracts]))
      )
    );

    dispatch(
      setSelectedVehicles(
        Array.from(new Set([...selectedVehicles, ...vehicles]))
      )
    );
  };

  const toggleSelectVehicles = (id: string) => {
    const next = selectedVehicles.includes(id)
      ? selectedVehicles.filter(item => item !== id)
      : [...selectedVehicles, id];

    dispatch(setSelectedVehicles(next));
  };

  const collectAllIds = (data: IRegionalProps[]) => {
    const regionals: string[] = [];
    const clients: string[] = [];
    const contracts: string[] = [];
    const vehicles: string[] = [];

    data.forEach(regional => {
      regionals.push(regional.id);

      regional.clients.forEach(client => {
        clients.push(client.id);

        client.contracts.forEach(contract => {
          contracts.push(contract.id);

          contract.vehicles.forEach(vehicle => {
            vehicles.push(vehicle.id);
          });
        });
      });
    });

    return {
      regionals,
      clients,
      contracts,
      vehicles,
    };
  };

  const selectAll = () => {
    if(!filteredData) return;
    const { regionals, clients, contracts, vehicles } = collectAllIds(filteredData);

    dispatch(setSelectedRegional(regionals));
    dispatch(setSelectedClients(clients));
    dispatch(setSelectedContracts(contracts));
    dispatch(setSelectedVehicles(vehicles));
  };

  const unselectAll = () => {
    dispatch(setSelectedRegional([]));
    dispatch(setSelectedClients([]));
    dispatch(setSelectedContracts([]));
    dispatch(setSelectedVehicles([]));
  };

  const hasItemSelected = selectedRegional.length +
    selectedClients.length +
    selectedContracts.length +
    selectedVehicles.length;

  return (
    <div className="display-flex bg-white height-100vh">
      <div className="display-flex justify-content-center bg-black width-50 padding-top-20 position-relative">
        <div className="">
          <div className="display-inline-block position-relative">
            {hasItemSelected ? (
              <div className="width-10 height-10 bg-success rounded-circle position-absolute top--3 right-0 z-index-max border border-width-1 border-color-black"></div>
            ) : null}
            <span onClick={() => setShowAsset(!showAsset)} className="rioglyph rioglyph-truck text-size-h3 cursor-pointer"></span>
          </div>
          <span
            className={`
              rioglyph rioglyph-triangle-left position-absolute right--6 top-25 text-size-18
              ${showAsset ? 'display-inline-block' : 'display-none'}
            `}
            aria-hidden="true"
          ></span>
        </div>
      </div>

      <div className={`
        width-300 display-flex flex-column padding-top-0
        ${showAsset ? 'display-block' : 'display-none'}
      `}>
        {loading ? (
          <Spinner />
        ) : null}

        {!loading ? (
          <>
            <AssetSearch search={search} setSearch={setSearch}/>
            <AssetHeader assetData={filteredData} selectAll={selectAll} unselectAll={unselectAll} />

            <div className="padding-top-20 flex-1 min-height-0 overflow-y-auto">
              {filteredData?.map(regional => {
                const totalVehicles = regional.clients.reduce(
                  (clientSum, client) =>
                    clientSum +
                    client.contracts.reduce(
                      (contractSum, contract) =>
                        contractSum + contract.vehicles.length,
                      0
                    ),
                  0
                )

                return (
                  <TreeNode
                    key={regional.id}
                    id={regional.id}
                    expandId={regional.id}
                    label={regional.name}
                    icon="rioglyph-role-management"
                    type="regional"
                    totalVehicles={totalVehicles}
                    level={1}
                    expanded={expanded}
                    selected={selectedRegional}
                    toggleExpand={toggleExpand}
                    toggleSelect={toggleSelectRegional}
                  >
                    {regional.clients.map(client => (
                      <TreeNode
                        key={`${regional.id}-${client.id}`}
                        id={client.id}
                        expandId={`${regional.id}-${client.id}`}
                        label={client.name}
                        icon="rioglyph-transfer"
                        level={2}
                        expanded={expanded}
                        selected={selectedClients}
                        toggleExpand={toggleExpand}
                        toggleSelect={toggleSelectClients}
                      >
                        {client.contracts.map(contract => (
                          <TreeNode
                            key={`${regional.id}-${client.id}-${contract.id}`}
                            id={contract.id}
                            expandId={`${regional.id}-${client.id}-${contract.id}`}
                            label={contract.name}
                            icon="rioglyph-file-signature"
                            level={3}
                            expanded={expanded}
                            selected={selectedContracts}
                            toggleExpand={toggleExpand}
                            toggleSelect={toggleSelectContracts}
                          >
                          {contract.vehicles.map(vehicle => (
                            <TreeNode
                              key={`${regional.id}-${client.id}-${contract.id}-${vehicle.id}`}
                              id={vehicle.id}
                              expandId={`${regional.id}-${client.id}-${contract.id}-${vehicle.id}`}
                              label={vehicle.name}
                              icon="rioglyph-truck"
                              level={4}
                              expanded={expanded}
                              selected={selectedVehicles}
                              toggleExpand={toggleExpand}
                              toggleSelect={toggleSelectVehicles}
                            />
                          ))}
                        </TreeNode>
                      ))}
                    </TreeNode>
                  ))}
                </TreeNode>
                )
              })}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default CustomAssetTree
